import { useEffect, useId, useRef, useState } from 'react'
import Icon from '../../docs/Icon'

export type Language = {
  /** A BCP 47 tag, such as "es". */
  lang: string
  /** The language's name in that language, such as "Español". */
  name: string
  /** The name in English, for the accessible label. */
  english: string
}

export type LanguageSelectorProps = {
  languages: Language[]
  value: string
  onChange: (lang: string) => void
}

/**
 * Switches the page language. With two languages it is a single button that
 * names the other one; with more, a menu. Each name is written in its own
 * language and marked with lang, so screen readers pronounce it correctly.
 */
export default function LanguageSelector({ languages, value, onChange }: LanguageSelectorProps) {
  const menuId = useId()
  const [open, setOpen] = useState(false)
  const root = useRef<HTMLDivElement>(null)
  const button = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!open) return
    root.current?.querySelector<HTMLButtonElement>('[role="menuitemradio"]')?.focus()
    const onPointer = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('pointerdown', onPointer)
    return () => document.removeEventListener('pointerdown', onPointer)
  }, [open])

  if (languages.length === 2) {
    const other = languages.find((language) => language.lang !== value) ?? languages[0]
    return (
      <button type="button" className="mds-language mds-language__toggle" lang={other.lang} onClick={() => onChange(other.lang)}>
        <Icon name="globe" size={16} />
        {other.name}
        <span className="mds-visually-hidden" lang="en">
          , switch to {other.english}
        </span>
      </button>
    )
  }

  const current = languages.find((language) => language.lang === value) ?? languages[0]
  const choose = (lang: string) => {
    onChange(lang)
    setOpen(false)
    button.current?.focus()
  }

  return (
    <div ref={root} className="mds-language">
      <button
        ref={button}
        type="button"
        className="mds-language__toggle"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        onClick={() => setOpen((state) => !state)}
      >
        <Icon name="globe" size={16} />
        <span lang={current.lang}>{current.name}</span>
        <span className="mds-visually-hidden">, choose language</span>
      </button>
      <div
        id={menuId}
        role="menu"
        aria-label="Languages"
        className="mds-language__menu"
        hidden={!open}
        onKeyDown={(event) => {
          const items = Array.from(event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="menuitemradio"]'))
          const index = items.indexOf(document.activeElement as HTMLButtonElement)
          if (event.key === 'ArrowDown') items[(index + 1) % items.length]?.focus()
          else if (event.key === 'ArrowUp') items[(index - 1 + items.length) % items.length]?.focus()
          else if (event.key === 'Escape') {
            setOpen(false)
            button.current?.focus()
          } else if (event.key === 'Tab') {
            // Tab leaves the menu, so it closes and focus moves on as usual.
            setOpen(false)
            return
          } else return
          event.preventDefault()
        }}
      >
        {languages.map((language) => (
          <button
            key={language.lang}
            type="button"
            role="menuitemradio"
            aria-checked={language.lang === value}
            lang={language.lang}
            className="mds-language__item"
            tabIndex={-1}
            onClick={() => choose(language.lang)}
          >
            {language.name}
            {language.english !== language.name && (
              <span className="mds-hint" lang="en">
                {language.english}
              </span>
            )}
          </button>
        ))}
      </div>
    </div>
  )
}
